import { useEffect, useState } from "react";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, Clock, ShieldAlert, Sparkles } from "lucide-react";

const STATUS_LABEL = {
  authorized: "Activa",
  trialing: "Periodo de prueba",
  trial_expired: "Prueba vencida",
  past_due: "Pago pendiente",
  cancelled: "Cancelada",
  none: "Sin suscripción",
};

export default function SubscriptionView() {
  const { user, refreshAccess } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/billing/status");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const subscribe = async () => {
    if (!data?.mp_configured) {
      toast.info("MercadoPago aún está en configuración. Solicita una liberación temporal al administrador de la plataforma.");
      return;
    }
    setBusy(true);
    try {
      // Requires MercadoPago.js tokenization (loaded when public key is available).
      toast.info("Redirigiendo a MercadoPago…");
      // The card-token flow is wired on the backend (/billing/subscribe); front-end
      // tokenization is enabled once MP_PUBLIC_KEY is configured.
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!data) return <div className="p-10 text-sm text-muted-foreground">Cargando suscripción…</div>;

  const access = data.access || {};
  const active = access.active;
  const reason = access.reason;

  return (
    <div className="flex-1 overflow-auto p-6" data-testid="subscription-view">
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary" />
          <h1 className="font-head font-black text-xl tracking-tight">Suscripción</h1>
        </div>

        <div className={`border rounded-sm p-5 ${active ? "border-primary/40 bg-primary/5" : "border-destructive/40 bg-destructive/10"}`} data-testid="subscription-status-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Estado</div>
              <div className="flex items-center gap-2 mt-1">
                {active ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <ShieldAlert className="h-5 w-5 text-destructive" />}
                <span className="text-lg font-medium" data-testid="subscription-status">{STATUS_LABEL[access.status] || access.status}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Plan</div>
              <div className="font-medium mt-1">{data.plan_name}</div>
              <div className="font-mono-num text-primary text-lg">$ {fmt(data.price_mxn, 2)} <span className="text-xs text-muted-foreground">MXN / año</span></div>
            </div>
          </div>

          {reason === "trial" && (
            <div className="mt-4 flex items-center gap-2 text-sm text-amber-500" data-testid="trial-info">
              <Clock className="h-4 w-4" /> Prueba gratis: <span className="font-mono-num font-medium">{access.days_left}</span> días restantes.
            </div>
          )}
          {reason === "admin_grant" && (
            <div className="mt-4 flex items-center gap-2 text-sm text-emerald-500">
              <Sparkles className="h-4 w-4" /> Acceso liberado por el administrador de la plataforma.
            </div>
          )}
          {!active && (
            <div className="mt-4 text-sm text-destructive" data-testid="no-access-info">
              Sin suscripción activa: la aplicación está en modo lectura. Crear/editar presupuestos, exportar y la IA están bloqueados.
            </div>
          )}
        </div>

        <div className="border border-border rounded-sm p-5 space-y-3">
          <h2 className="text-base font-medium">Suscríbete a {data.plan_name}</h2>
          <p className="text-sm text-muted-foreground">
            Acceso completo: presupuestos ilimitados, FASAR, exportación PDF/Excel, programa de obra, minería y el Cazador de Licitaciones IA.
          </p>
          <ul className="text-sm space-y-1">
            <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> $ {fmt(data.price_mxn, 2)} MXN / año</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Renovación automática vía MercadoPago</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Multiusuario por empresa</li>
          </ul>
          {user?.role === "admin" ? (
            <Button onClick={subscribe} disabled={busy} className="rounded-sm gap-2" data-testid="subscribe-btn">
              <CreditCard className="h-4 w-4" /> {data.mp_configured ? "Suscribirme ahora" : "Suscribirme (configuración pendiente)"}
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">Solo el administrador de tu empresa puede gestionar la suscripción.</p>
          )}
          {!data.mp_configured && (
            <p className="text-xs text-amber-500" data-testid="mp-pending">
              El cobro con MercadoPago se habilitará en cuanto se configuren las credenciales. Mientras tanto, el administrador de la plataforma puede liberar el acceso.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
